"use client";

import { cn } from "@jesus-central-church/shared";
import { AnimatePresence, motion } from "motion/react";
import { ChevronLeft, ChevronRight, Clock, MapPin, Users } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "../badge";
import { Button } from "../button";
import { Heading } from "../heading";
import type { EventUI } from "./types";
import { formatEventDate, getEventTypeLabel } from "./types";

export interface EventsHeroCarouselProperties {
  events: EventUI[];
  autoplay?: boolean;
  intervalMs?: number;
  className?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyButtonText?: string;
  registrationText?: string;
  seeDetailsText?: string;
}

export function EventsHeroCarousel({
  events,
  autoplay = true,
  intervalMs = 6000,
  className,
  emptyTitle = "Geen events gepland",
  emptyDescription = "Er staan momenteel geen events op de planning.",
  emptyButtonText = "Neem contact op",
  registrationText = "Aanmelden",
  seeDetailsText = "Bekijk details",
}: EventsHeroCarouselProperties) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const featuredEvents = events.filter((event) => event.featured).slice(0, 5);
  const displayEvents =
    featuredEvents.length > 0 ? featuredEvents : events.slice(0, 5);

  const nextSlide = useCallback(() => {
    setCurrentIndex((previous) => (previous + 1) % displayEvents.length);
  }, [displayEvents.length]);

  const previousSlide = useCallback(() => {
    setCurrentIndex(
      (previous) =>
        (previous - 1 + displayEvents.length) % displayEvents.length,
    );
  }, [displayEvents.length]);

  useEffect(() => {
    if (!autoplay || isPaused || displayEvents.length <= 1) return;
    const interval = setInterval(nextSlide, intervalMs);
    return () => clearInterval(interval);
  }, [autoplay, isPaused, displayEvents.length, nextSlide, intervalMs]);

  // Empty state
  if (displayEvents.length === 0) {
    return (
      <div
        className={cn(
          "relative flex h-[70vh] min-h-[500px] items-center justify-center bg-boldness",
          className,
        )}
      >
        <div className="text-center text-freedom">
          <Heading variant="h2" className="mb-4 text-freedom">
            {emptyTitle}
          </Heading>
          <p className="mb-8 opacity-70">{emptyDescription}</p>
          <Button href="/contact" colorScheme="freedom" variant="outline">
            {emptyButtonText}
          </Button>
        </div>
      </div>
    );
  }

  const currentEvent = displayEvents[currentIndex];
  const { day, monthLong, time, dayName } = formatEventDate(
    currentEvent.startDateTime,
  );
  const typeLabel =
    currentEvent.typeLabel || getEventTypeLabel(currentEvent.type);

  return (
    <div
      className={cn(
        "relative h-[80vh] min-h-[600px] overflow-hidden bg-boldness",
        className,
      )}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Image */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentEvent.id}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7 }}
          className="absolute inset-0"
        >
          <Image
            src={
              currentEvent?.coverImage?.src ?? "/default-event-background.jpg"
            }
            alt={currentEvent.title}
            fill
            className="absolute inset-0 object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-boldness/95 via-boldness/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-boldness/80 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="container relative z-10 mx-auto flex h-full items-center px-6 md:px-12">
        <div className="grid w-full grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16">
          {/* Left Column - Main Content */}
          <div className="flex flex-col justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentEvent.id}
                initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                {/* Label */}
                <div className="mb-4 flex items-center gap-3">
                  <span className="text-strategy-gold text-sm font-bold tracking-[0.3em] uppercase">
                    Next Up
                  </span>
                  <motion.div
                    className="bg-strategy-gold h-[2px] w-12"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  />
                </div>

                {/* Title */}
                <h1 className="mb-6 font-extrabold text-5xl tracking-tight text-freedom uppercase sm:text-6xl lg:text-7xl">
                  {currentEvent.title}
                </h1>

                {/* Excerpt */}
                {currentEvent.excerpt && (
                  <p className="mb-8 max-w-lg text-lg text-freedom/70 lg:text-xl">
                    {currentEvent.excerpt}
                  </p>
                )}

                {/* CTAs */}
                <div className="flex flex-wrap gap-4">
                  {currentEvent.weeztixShopUrl ? (
                    <Button
                      href={currentEvent.weeztixShopUrl}
                      colorScheme="strategy-gold"
                      size="large"
                      showArrowIcon
                    >
                      {registrationText}
                    </Button>
                  ) : (
                    <Button
                      href={`/${currentEvent.slug}`}
                      colorScheme="strategy-gold"
                      size="large"
                      showArrowIcon
                    >
                      {seeDetailsText}
                    </Button>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right Column - Meta Info */}
          <div className="hidden items-center justify-end lg:flex">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentEvent.id}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="space-y-6"
              >
                {/* Date Block */}
                <div className="border-freedom/20 bg-boldness/50 rounded-2xl border p-6 backdrop-blur-sm">
                  <div className="flex items-start gap-6">
                    <div className="text-center">
                      <span className="text-strategy-gold block text-sm font-bold uppercase">
                        {dayName}
                      </span>
                      <span className="block font-extrabold text-6xl text-freedom">
                        {day}
                      </span>
                      <span className="text-freedom/70 block text-sm uppercase">
                        {monthLong}
                      </span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-freedom/80">
                        <Clock className="h-4 w-4" />
                        <span>{time}</span>
                      </div>
                      {currentEvent.location && (
                        <div className="flex items-center gap-2 text-freedom/80">
                          <MapPin className="h-4 w-4" />
                          <span>{currentEvent.location}</span>
                        </div>
                      )}
                      {currentEvent.speaker && (
                        <div className="flex items-center gap-2 text-freedom/80">
                          <Users className="h-4 w-4" />
                          <span>{currentEvent.speaker}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {typeLabel && (
                  <div className="flex flex-wrap gap-2">
                    <Badge
                      text={typeLabel}
                      backgroundColor="strategy-gold"
                      textColor="freedom"
                    />
                  </div>
                )}

                {/* Slide Counter */}
                <div className="text-freedom/50 text-right font-mono text-sm">
                  {String(currentIndex + 1).padStart(2, "0")} /{" "}
                  {String(displayEvents.length).padStart(2, "0")}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {displayEvents.length > 1 && (
        <>
          <button
            onClick={previousSlide}
            className="absolute left-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-freedom/10 p-3 text-freedom backdrop-blur-sm transition-all hover:bg-freedom/20"
            aria-label="Vorige"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 z-20 -translate-y-1/2 rounded-full bg-freedom/10 p-3 text-freedom backdrop-blur-sm transition-all hover:bg-freedom/20"
            aria-label="Volgende"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 z-20 flex gap-1 px-6 pb-6">
        {displayEvents.map((_, index) => (
          <button
            key={displayEvents[index].id}
            onClick={() => setCurrentIndex(index)}
            className="h-1 flex-1 overflow-hidden rounded-full bg-freedom/20"
            aria-label={`Ga naar slide ${index + 1}`}
          >
            <motion.div
              className="bg-strategy-gold h-full"
              initial={{ width: "0%" }}
              animate={{
                width: index === currentIndex ? "100%" : "0%",
              }}
              transition={{
                duration: index === currentIndex ? intervalMs / 1000 : 0,
                ease: "linear",
              }}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
